import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { VERIFY_OTP, RESEND_OTP } from "@/Graphql/otpQueries";
import { useToast } from '@/hooks/use-toast';

interface EmailVerificationProps {
  yourLogo?: string;
}

interface LocationState {
  userId: string;
  email: string;
  companyId: string;
  expirySecond?: number;
}

const EmailVerification: React.FC<EmailVerificationProps> = ({ yourLogo }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  // Get user data from navigation state
  const locationState = location.state as LocationState;
  
  // Redirect if no user data
  useEffect(() => {
    if (!locationState?.userId) {
      navigate('/sign-up');
    }
  }, [locationState, navigate]);

  const [otp, setOtp] = useState("");
  const [timeLeft, setTimeLeft] = useState(locationState?.expirySecond || 300); // Default 5 minutes
  const [canResend, setCanResend] = useState(false);

  // Mutations
  const [verifyOTP, { loading: isVerifying }] = useMutation(VERIFY_OTP);
  const [resendOTP, { loading: isResending }] = useMutation(RESEND_OTP);

  // Timer effect
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [timeLeft]);

  // Format time display
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast({
        title: "Invalid OTP",
        description: "Please enter a 6-digit OTP",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await verifyOTP({
        variables: {
          userId: locationState.userId,
          otpCode: otp,
          event: "REGISTRATION"
        }
      });

      if (response.data?.verifyOtp?.success) {
        toast({
          title: "Success",
          description: "Email verified successfully! You can now login.",
          variant: "default",
        });
        navigate("/login");
      } else {
        // Check for GraphQL errors in multiple locations
        let errorMessage = "Invalid OTP. Please try again.";
        
        // Check response.errors first (GraphQL errors)
        if (response.errors && response.errors.length > 0) {
          errorMessage = response.errors[0].message;
        } 
        // Check response.data.verifyOtp.message
        else if (response.data?.verifyOtp?.message) {
          errorMessage = response.data.verifyOtp.message;
        }
        
        // Enable resend if max attempts reached, OTP expired, or no active OTP found
        if (errorMessage.toLowerCase().includes("maximum") || 
            errorMessage.toLowerCase().includes("exceeded") ||
            errorMessage.toLowerCase().includes("no active otp") ||
            errorMessage.toLowerCase().includes("expired")) {
          setCanResend(true);
          setTimeLeft(0);
        }
        
        toast({
          title: "Verification Failed",
          description: errorMessage,
          variant: "destructive",
        });
        setOtp(""); // Clear OTP input
      }
    } catch (error: any) {
      console.error("OTP verification error:", error);
      let errorMessage = "Failed to verify OTP. Please try again.";
      
      // Check for GraphQL errors in Apollo error object
      if (error?.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMessage = error.graphQLErrors[0].message;
      } 
      // Check for network errors
      else if (error?.networkError?.result?.errors && error.networkError.result.errors.length > 0) {
        errorMessage = error.networkError.result.errors[0].message;
      }
      // Check for generic message
      else if (error?.message && !error.message.includes("400")) {
        errorMessage = error.message;
      }
      
      // Enable resend if max attempts reached, OTP expired, or no active OTP found
      if (errorMessage.toLowerCase().includes("maximum") || 
          errorMessage.toLowerCase().includes("exceeded") ||
          errorMessage.toLowerCase().includes("no active otp") ||
          errorMessage.toLowerCase().includes("expired")) {
        setCanResend(true);
        setTimeLeft(0);
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      setOtp(""); // Clear OTP input
    }
  };

  const handleResendOTP = async () => {
    try {
      const response = await resendOTP({
        variables: {
          userId: locationState.userId,
          event: "REGISTRATION",
          channel: "EMAIL"
        }
      });

      if (response.data?.resendOtp?.success) {
        toast({
          title: "Success",
          description: "OTP sent successfully to your email",
          variant: "default",
        });
        setTimeLeft(response.data.resendOtp.expirySecond || 300);
        setCanResend(false);
        setOtp(""); // Clear current OTP
      } else {
        toast({
          title: "Failed",
          description: response.data?.resendOtp?.message || "Failed to resend OTP",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      toast({
        title: "Error",
        description: "Failed to resend OTP. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Don't render if no user data
  if (!locationState?.userId) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="p-8">
          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <Link to="/" className="flex items-center">
              {yourLogo ? (
                <img
                  src={yourLogo}
                  alt="Logo"
                  className="h-10 w-auto"
                  style={{ maxWidth: 160 }}
                />
              ) : (
                <>
                  <div className="w-10 h-10 bg-gradient-to-r from-emerald-600 to-emerald-400 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2L2 7v10c0 5.55 3.84 10 9 11 5.16-1 9-5.45 9-11V7l-10-5zM12 9c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3z" />
                    </svg>
                  </div>
                  <span className="ml-3 text-xl font-semibold text-gray-900">BillboardHub</span>
                </>
              )}
            </Link>
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-emerald-600 mb-2">
              Email Verification
            </h1>
            <p className="text-gray-600">
              We've sent a 6-digit verification code to
            </p>
            <p className="font-semibold text-gray-900">
              {locationState.email}
            </p>
          </div>

          {/* OTP Input */}
          <div className="mb-6">
            <div className="flex justify-center mb-4">
              <InputOTP
                maxLength={6}
                value={otp}
                onChange={(value) => {
                  // Accept only numbers
                  const numericValue = value.replace(/[^0-9]/g, "");
                  setOtp(numericValue);
                }}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            {/* Timer */}
            <div className="text-center text-sm text-gray-600 mb-4">
              {timeLeft > 0 ? (
                <span>Code expires in {formatTime(timeLeft)}</span>
              ) : (
                <span className="text-red-500">Code has expired</span>
              )}
            </div>
          </div>

          {/* Verify Button */}
          <Button
            onClick={handleVerifyOTP}
            disabled={isVerifying || otp.length !== 6}
            className="w-full h-12 btn-emerald-gradient disabled:opacity-50 text-white font-semibold rounded-lg mb-4"
          >
            {isVerifying ? "Verifying..." : "Verify Email"}
          </Button>

          {/* Resend Button */}
          <div className="text-center">
            <span className="text-sm text-gray-600">Didn't receive the code? </span>
            <Button
              variant="link"
              onClick={handleResendOTP}
              disabled={!canResend || isResending}
              className="p-0 h-auto text-emerald-600 hover:underline disabled:opacity-50"
            >
              {isResending ? "Sending..." : "Resend OTP"}
            </Button>
          </div>

          {/* Back to Registration Link */}
          <div className="text-center text-sm text-gray-600 mt-6">
            <Link to="/sign-up" className="text-emerald-600 hover:underline">
              Back to Registration
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;